import React, { useState, useEffect } from "react";
import Collapse from "react-collapse";
import "../styles.css";
import {
  listarArchivosMantenimiento,
  subirArchivoMantenimiento,
  eliminarArchivoMantenimiento,
  borrarMantenimiento,
} from "../api";
import { toast } from "react-toastify";
import { useConfirm } from "../ConfirmContext";

function MantenimientoCard({
  mantenimiento,
  onEditar,
  onEliminar,
  almacenItems,
  puedeEditar = false,
  puedeBorrar = false,
}) {
  const [expandido, setExpandido] = useState(false);
  const [archivos, setArchivos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [cargandoArchivos, setCargandoArchivos] = useState(false);
  const [errorArchivos, setErrorArchivos] = useState(null);
  const confirm = useConfirm();

  const handleClick = () => {
    setExpandido(!expandido);
  };

  const handleEliminarClick = async (e) => {
    e.stopPropagation();
    if (!puedeBorrar) {
      toast.warn("Solo administradores pueden eliminar mantenimientos");
      return;
    }
    const isConfirmed = await confirm({
      title: "¿Eliminar mantenimiento?",
      message: "¿Estás seguro de que deseas eliminar este mantenimiento? Esta acción no se puede deshacer.",
    });
    if (!isConfirmed) return;
    try {
      await borrarMantenimiento(mantenimiento.id);
      onEliminar?.(mantenimiento.id);
      toast.success("Mantenimiento eliminado");
    } catch (err) {
      toast.error("Error eliminando mantenimiento: " + err.message);
    }
  };

  useEffect(() => {
    if (!expandido) return;
    const fetchArchivos = async () => {
      setCargandoArchivos(true);
      setErrorArchivos(null);
      try {
        const data = await listarArchivosMantenimiento(mantenimiento.id);
        setArchivos(data);
      } catch (err) {
        console.error("Error cargando archivos:", err);
        setErrorArchivos("No se pudieron cargar los archivos.");
      } finally {
        setCargandoArchivos(false);
      }
    };
    fetchArchivos();
  }, [expandido, mantenimiento.id]);

  const handleSubirArchivo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setSubiendo(true);
      await subirArchivoMantenimiento(mantenimiento.id, file);
      const nuevos = await listarArchivosMantenimiento(mantenimiento.id);
      setArchivos(nuevos);
    } catch (err) {
      toast.error("Error subiendo archivo: " + err.message);
    } finally {
      setSubiendo(false);
      e.target.value = null;
    }
  };

  const handleEliminarArchivo = async (id) => {
    const isConfirmed = await confirm({
      title: "¿Eliminar archivo?",
      message: "¿Deseas eliminar permanentemente este archivo?",
    });
    if (!isConfirmed) return;
    try {
      await eliminarArchivoMantenimiento(id);
      const nuevos = await listarArchivosMantenimiento(mantenimiento.id);
      setArchivos(nuevos);
      toast.success("Archivo eliminado");
    } catch (err) {
      toast.error("Error eliminando archivo: " + err.message);
    }
  };

  const equipoNombre =
    mantenimiento.vehiculo?.nombre ||
    mantenimiento.maquinaria?.nombre ||
    mantenimiento.utillaje?.nombre ||
    "";

  const matricula =
    mantenimiento.vehiculo?.matricula ||
    mantenimiento.maquinaria?.matricula ||
    mantenimiento.utillaje?.matricula ||
    "";

  const tipoEquipo =
    mantenimiento.vehiculo
      ? "Vehículo"
      : mantenimiento.maquinaria
      ? "Maquinaria"
      : mantenimiento.utillaje
      ? "Utillaje"
      : "";

  const fecha =
    mantenimiento.fecha
      ? new Date(mantenimiento.fecha).toLocaleDateString("es-ES")
      : "";

  const observaciones = mantenimiento?.observaciones ?? "";

  const detallesCompletos = mantenimiento.detalles?.map((d) => ({
    ...d,
    item:
      d.item ||
      almacenItems?.find((i) => i.id === d.almacenid)?.nombre ||
      "Item desconocido",
    referencia:
      d.item?.referencia ||
      almacenItems?.find((i) => i.id === d.almacenid)?.referencia ||
      "",
  })) || [];

  let kmHorasMantenimiento = "";
  if (mantenimiento.vehiculo?.kmactual != null) {
    kmHorasMantenimiento = mantenimiento.kmactual;
  } else if (mantenimiento.maquinaria?.horasactuales != null) {
    kmHorasMantenimiento = mantenimiento.horasactuales;
  }

  const labelKmHoras =
    mantenimiento.vehiculo
      ? "Km al mantenimiento"
      : mantenimiento.maquinaria
      ? "Horas al mantenimiento"
      : "";

  return (
    <div className="mantenimiento-card">
      <div className="fila-item" onClick={handleClick}>
        <div className="col-titulo">{equipoNombre}</div>
        <div className="col-matricula">{matricula}</div>
        <div className="col-fecha">{fecha}</div>
        <div className="col-descripcion">{mantenimiento.descripcion}</div>
        <div className="col-acciones">
          <button
            className="botonEditar"
            onClick={(e) => {
              e.stopPropagation();
              if (puedeEditar) onEditar?.(mantenimiento);
            }}
            disabled={!puedeEditar}
            title={!puedeEditar ? "Solo usuarios pueden editar" : "Editar"}
          >
            Editar
          </button>
          <button
            className="botonEliminar"
            onClick={handleEliminarClick}
            disabled={!puedeBorrar}
            title={!puedeBorrar ? "Solo administradores pueden eliminar" : "Eliminar"}
          >
            Eliminar
          </button>
        </div>
      </div>

      <Collapse isOpened={expandido}>
        <div className="detalle-mantenimiento" style={{ display: "flex", gap: "20px" }}>
          <div style={{ flex: 1, paddingBottom: "20px" }}>
            <p>
              <b>Nombre:</b> {equipoNombre}
            </p>
            <p>
              <b>Matrícula / SN:</b> {matricula}
            </p>
            <p>
              <b>Tipo:</b> {tipoEquipo}
            </p>
            <p>
              <b>Fecha:</b> {fecha}
            </p>
            <p>
              <b>Descripción:</b> {mantenimiento.descripcion}
            </p>
            <p>
              <b>Observaciones:</b> {observaciones}
            </p>
            {labelKmHoras && kmHorasMantenimiento && (
              <p>
                <b>{labelKmHoras}:</b> {kmHorasMantenimiento}
              </p>
            )}
            {detallesCompletos.length > 0 && (
              <div style={{ marginTop: "10px" }}>
                <b>Materiales usados:</b>
                <ul>
                  {detallesCompletos.map((d) => (
                    <li key={d.id}>
                      {d.item.nombre} {d.item.referencia} - Cantidad: {d.cantidadusada}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div style={{ flex: 1 }}>
            <b>Archivos del mantenimiento:</b>
            <div className="input-archivo" style={{ margin: "10px 0" }}>
              <button
                className="botonAzul"
                onClick={() =>
                  document.getElementById(`file-input-${mantenimiento.id}`).click()
                }
                disabled={subiendo}
              >
                {subiendo ? "Subiendo..." : "Seleccionar archivo"}
              </button>
              <input
                id={`file-input-${mantenimiento.id}`}
                type="file"
                onChange={handleSubirArchivo}
                style={{ display: "none" }}
              />
            </div>
            {cargandoArchivos ? (
              <p>Cargando archivos...</p>
            ) : errorArchivos ? (
              <p style={{ color: "red" }}>{errorArchivos}</p>
            ) : archivos.length > 0 ? (
              <ul className="lista-archivos">
                {archivos.map((f) => (
                  <li key={f.id} className="archivo-item">
                    <a href={f.ruta} target="_blank" rel="noopener noreferrer">
                      {f.nombre}
                    </a>
                    <button
                      className="botonEliminar"
                      onClick={() => handleEliminarArchivo(f.id)}
                    >
                      Eliminar
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No hay archivos en este mantenimiento</p>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

export default MantenimientoCard;
